import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Spinner,
  TextInput
} from "@patternfly/react-core";

import { apiGet, apiPost } from "../../app/api";
import { compactDateTime, locationLabel } from "../../display";
import type { Location, LocationsPayload, ViewKey } from "../../types";
import { StandardDataTable, type StandardColumn } from "../../components/StandardDataTable";

type AccessUser = {
  id: number;
  username: string;
  full_name: string;
  role: string;
  location_id?: number | null;
  location_code?: string;
  location_name?: string;
  active?: number;
  last_login_at?: string;
  tabs?: ViewKey[];
  explicit_tabs?: ViewKey[];
  available_tabs?: Partial<Record<ViewKey, string>>;
};

type AccessRole = {
  id: number;
  name: string;
  permissions?: string[];
  tabs?: ViewKey[];
  available_tabs?: Partial<Record<ViewKey, string>>;
};

const FALLBACK_TABS: Array<{ key: ViewKey; label: string }> = [
  { key: "dashboard", label: "Today" },
  { key: "po_request", label: "Branch Requests" },
  { key: "vendor_po", label: "Vendor Purchasing" },
  { key: "production", label: "Dispatch Control" },
  { key: "store_inventory", label: "Inventory Actions" },
  { key: "add_inventory", label: "Add to Inventory" },
  { key: "store_count", label: "Inventory Count" },
  { key: "manual_adjustments", label: "Waste / Loss Reporting" },
  { key: "finance_close", label: "Finance Close" },
  { key: "sales", label: "POS & Sales" },
  { key: "report_daily_inventory", label: "Daily Inventory" },
  { key: "report_all_branches_inventory", label: "All Branches Inventory" },
  { key: "report_product_sales_count", label: "Product Sales Count" },
  { key: "user_access", label: "User Access" },
  { key: "admin", label: "Master Setup" },
  { key: "audit", label: "Audit Trail" },
  { key: "sync_status", label: "Offline Uploads" },
  { key: "db_edits", label: "POS Item Links" }
];

function activeLabel(value: number | undefined): string {
  return Number(value ?? 1) ? "Active" : "Disabled";
}

function tabLabel(tab: ViewKey, availableTabs?: Partial<Record<ViewKey, string>>): string {
  return availableTabs?.[tab] || FALLBACK_TABS.find(item => item.key === tab)?.label || tab;
}

function locationText(row: AccessUser): string {
  return [row.location_code, row.location_name].filter(Boolean).join(" - ");
}

export function UserAccessWorkspace({ csrfToken }: { csrfToken: string }) {
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [editing, setEditing] = useState<AccessUser | null>(null);
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [roleId, setRoleId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [active, setActive] = useState("1");
  const [selectedTabs, setSelectedTabs] = useState<Set<ViewKey>>(new Set());
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const loadAccess = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [userPayload, rolePayload, locationPayload] = await Promise.all([
        apiGet<{ users?: AccessUser[] }>("/api/users"),
        apiGet<{ roles?: AccessRole[] }>("/api/roles"),
        apiGet<LocationsPayload>("/api/locations")
      ]);
      setUsers(userPayload.users || []);
      setRoles(rolePayload.roles || []);
      setLocations(locationPayload.locations || []);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "User Access could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAccess();
  }, [loadAccess]);

  const activeRole = roles.find(role => String(role.id) === roleId);
  const availableTabs = useMemo(() => {
    const roleTabs = activeRole?.available_tabs ? Object.entries(activeRole.available_tabs).map(([key, label]) => ({ key: key as ViewKey, label: label || key })) : [];
    if (roleTabs.length) return roleTabs;
    const userTabs = editing?.available_tabs ? Object.entries(editing.available_tabs).map(([key, label]) => ({ key: key as ViewKey, label: label || key })) : [];
    return userTabs.length ? userTabs : FALLBACK_TABS;
  }, [activeRole?.available_tabs, editing?.available_tabs]);

  const openEditor = useCallback((row?: AccessUser) => {
    const targetRole = row ? roles.find(role => role.name === row.role) : roles[0];
    setEditing(row || null);
    setUsername(row?.username || "");
    setFullName(row?.full_name || "");
    setPassword("");
    setRoleId(String(targetRole?.id || ""));
    setLocationId(String(row?.location_id || ""));
    setActive(String(Number(row?.active ?? 1)));
    setSelectedTabs(new Set(row?.tabs || targetRole?.tabs || ["dashboard"]));
    setEditorOpen(true);
  }, [roles]);

  const closeEditor = (): void => {
    setEditorOpen(false);
    setEditing(null);
    setUsername("");
    setFullName("");
    setPassword("");
    setRoleId("");
    setLocationId("");
    setActive("1");
    setSelectedTabs(new Set());
  };

  const toggleTab = (tab: ViewKey): void => {
    setSelectedTabs(current => {
      const next = new Set(current);
      if (next.has(tab)) next.delete(tab);
      else next.add(tab);
      return next;
    });
  };

  const saveUser = async (): Promise<void> => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean; user_id: number }>("/api/users", {
        username,
        full_name: fullName,
        password,
        role_id: roleId,
        location_id: locationId,
        active: Number(active),
        tabs: Array.from(selectedTabs)
      }, csrfToken);
      setMessage({ variant: "success", title: "User saved." });
      closeEditor();
      await loadAccess();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "User could not be saved." });
    } finally {
      setSaving(false);
    }
  };

  const setUserStatus = async (row: AccessUser, nextActive: number): Promise<boolean> => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean }>("/api/users/status", { user_id: row.id, active: nextActive }, csrfToken);
      setMessage({ variant: "success", title: nextActive ? "User enabled." : "User disabled." });
      await loadAccess();
      return true;
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "User status could not be changed." });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async (row: AccessUser): Promise<boolean> => {
    const nextPassword = window.prompt(`New password for ${row.username}`);
    if (!nextPassword) return false;
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean }>("/api/users/reset-password", { user_id: row.id, password: nextPassword }, csrfToken);
      setMessage({ variant: "success", title: `Password reset for ${row.username}.` });
      await loadAccess();
      return true;
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Password could not be reset." });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (row: AccessUser): Promise<boolean> => {
    if (!window.confirm(`Remove ${row.username} from active user management?`)) return false;
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean }>("/api/users/delete", { user_id: row.id }, csrfToken);
      setMessage({ variant: "success", title: "User removed from active management." });
      await loadAccess();
      return true;
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "User could not be removed." });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateEditingStatus = async (): Promise<void> => {
    if (!editing) return;
    const nextActive = Number(active) ? 0 : 1;
    const changed = await setUserStatus(editing, nextActive);
    if (!changed) return;
    setActive(String(nextActive));
    setEditing(current => current ? { ...current, active: nextActive } : current);
  };

  const deleteEditingUser = async (): Promise<void> => {
    if (!editing) return;
    const deleted = await deleteUser(editing);
    if (deleted) closeEditor();
  };

  const columns: StandardColumn<AccessUser>[] = useMemo(() => [
    { key: "username", label: "User", value: row => row.username, render: row => <><strong>{row.full_name}</strong><small>{row.username}</small></> },
    { key: "role", label: "Role", value: row => row.role },
    { key: "location", label: "Assigned Location", value: locationText },
    { key: "active", label: "Status", value: row => activeLabel(row.active), render: row => <Label color={Number(row.active ?? 1) ? "green" : "grey"}>{activeLabel(row.active)}</Label> },
    { key: "tabs", label: "Visible Workspaces", value: row => (row.tabs || []).map(tab => tabLabel(tab, row.available_tabs)).join(", ") },
    { key: "last_login_at", label: "Last Login", value: row => row.last_login_at || "", render: row => compactDateTime(row.last_login_at) },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: row => (
        <Flex spaceItems={{ default: "spaceItemsSm" }} className="pf-toj-row-actions">
          <FlexItem><Button variant="secondary" size="sm" onClick={() => openEditor(row)}>Edit</Button></FlexItem>
          <FlexItem><Button variant="secondary" size="sm" onClick={() => void resetPassword(row)}>Reset password</Button></FlexItem>
          <FlexItem><Button variant="secondary" size="sm" onClick={() => void setUserStatus(row, Number(row.active ?? 1) ? 0 : 1)}>{Number(row.active ?? 1) ? "Disable" : "Enable"}</Button></FlexItem>
          <FlexItem><Button variant="danger" size="sm" onClick={() => void deleteUser(row)}>Delete</Button></FlexItem>
        </Flex>
      )
    }
  ], [csrfToken, deleteUser, openEditor, resetPassword, setUserStatus]);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-user-access-view">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
            <h1>User Access</h1>
            <p>Manage user accounts, role assignments, location restrictions, and visible workspaces.</p>
          </FlexItem>
          <FlexItem>
            <Button variant="primary" onClick={() => openEditor()} isDisabled={loading || !roles.length}>
              Add user
            </Button>
          </FlexItem>
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <Card className="pf-toj-work-card pf-toj-primary-work-card pf-toj-user-access-card">
          <CardBody>
            {loading ? <Spinner size="lg" aria-label="Loading User Access" /> : (
              <StandardDataTable ariaLabel="User Access users" rows={users} columns={columns} searchPlaceholder="Search users, roles, locations, or workspaces" />
            )}
          </CardBody>
        </Card>
      </div>
      <Modal variant={ModalVariant.large} isOpen={editorOpen} onClose={closeEditor}>
        <ModalHeader title={editing ? `Edit ${editing.username}` : "Add user"} />
        <ModalBody>
          <Form className="pf-toj-form">
            <div className="pf-toj-field-row">
              <FormGroup label="Username" isRequired>
                <TextInput value={username} onChange={(_event, value) => setUsername(String(value))} aria-label="Username" />
              </FormGroup>
              <FormGroup label="Full name" isRequired>
                <TextInput value={fullName} onChange={(_event, value) => setFullName(String(value))} aria-label="Full name" />
              </FormGroup>
              <FormGroup label={editing ? "New password" : "Password"} isRequired={!editing}>
                <TextInput type="password" value={password} onChange={(_event, value) => setPassword(String(value))} aria-label="Password" />
              </FormGroup>
              <FormGroup label="Role" isRequired>
                <FormSelect value={roleId} onChange={(_event, value) => setRoleId(String(value))} aria-label="Role">
                  {roles.map(role => <FormSelectOption key={role.id} value={String(role.id)} label={role.name} />)}
                </FormSelect>
              </FormGroup>
              <FormGroup label="Branch restriction">
                <FormSelect value={locationId} onChange={(_event, value) => setLocationId(String(value))} aria-label="Branch restriction">
                  <FormSelectOption value="" label="No location restriction" />
                  {locations.map(location => <FormSelectOption key={location.id} value={String(location.id)} label={locationLabel(location)} />)}
                </FormSelect>
              </FormGroup>
              <FormGroup label="Active">
                <FormSelect value={active} onChange={(_event, value) => setActive(String(value))} aria-label="Active">
                  <FormSelectOption value="1" label="Yes" />
                  <FormSelectOption value="0" label="No" />
                </FormSelect>
              </FormGroup>
            </div>
            <div className="pf-toj-tab-check-grid" aria-label="Visible workspaces">
              {availableTabs.map(tab => (
                <label key={tab.key} className="pf-toj-check-tile">
                  <input type="checkbox" checked={selectedTabs.has(tab.key)} onChange={() => toggleTab(tab.key)} />
                  <span>{tab.label}</span>
                </label>
              ))}
            </div>
            {editing && (
              <section className="pf-toj-user-actions-panel" aria-label="Available user actions">
                <div>
                  <strong>Available actions</strong>
                  <span>Account actions for {editing.username}.</span>
                </div>
                <div className="pf-toj-user-actions-buttons">
                  <Button variant="secondary" onClick={() => void resetPassword(editing)} isDisabled={saving}>
                    Reset password
                  </Button>
                  <Button variant="secondary" onClick={() => void updateEditingStatus()} isDisabled={saving}>
                    {Number(active) ? "Disable user" : "Enable user"}
                  </Button>
                  <Button variant="danger" onClick={() => void deleteEditingUser()} isDisabled={saving}>
                    Delete user
                  </Button>
                </div>
              </section>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => void saveUser()} isLoading={saving} isDisabled={saving}>Save user</Button>
          <Button variant="secondary" onClick={closeEditor}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </PageSection>
  );
}
